import { useMsal } from '@azure/msal-react';
import { makeStyles, Typography } from '@material-ui/core';
import { default as React, useContext, useEffect, useState } from 'react';
import { getVerificationSessionCookieName } from '..';
import { AmplitudeEventType } from '../../../../../analytics/amplitudeEvents';
import { ClientConfig } from '../../../../../clientConfig';
import { SessionStatus } from '../../../../../router/types';
import { OPENID_PARAMS } from '../../../../../types/openid_enum';
import { ClientConfigContext } from '../../../../clientConfigContext';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { useBrand } from '../../../../hooks/useBrand';
import useConfigTranslate from '../../../../hooks/useConfigTranslate';
import useCookie, { setCookie } from '../../../../hooks/useCookie';
import { useValidatedUrl } from '../../../../hooks/useValidatedUrl';
import { getIdentityVerificationSession } from '../../../../services/pims/pimsClient';
import { useErrorHistoryPush } from '../../../../util/errorUtils';
import getAccessToken from '../../../../util/getAccessToken';
import BrandedCard, { BrandedCardActions, BrandedCardContent } from '../../../containers/BrandedCard_v2';
import withLogger, { ComponentLoggerProps } from '../../../HOC/WithLogger';
import { AuthenticatedRoutes } from '../../../routers';
import { toAnalyticsActivationMethodList } from '../analytics';
import { VENDOR_NAME } from '../clear';
import BulletPointsList from './components/BulletPointsList';
import CardActions from './components/CardActions';

type IdentityVerificationPageProps = ComponentLoggerProps & {
    isCrossRegion?: boolean;
    ehrSystem?: string;
};

enum PageState {
    Loading,
    NewSession,
    ExistingSession,
    Redirecting,
}

const useStyles = makeStyles(() => ({
    header: {
        marginBottom: 20,
    },
}));

/**
 * The Identity Verification Page.
 * Basic identities will arrive at this page when they need an EHR authorization.
 * @param isCrossRegion
 * @param log APIRequestLogger
 */
export const IdentityVerificationPage: React.FC<IdentityVerificationPageProps> = ({ isCrossRegion = false, log }: IdentityVerificationPageProps) => {
    const [jwt, setJwt] = useState('');
    const { instance, accounts } = useMsal();
    const { b2cTenantConfig, pimsApiUrl, identityVerification } = useContext<ClientConfig>(ClientConfigContext);
    const [token, setToken] = useState<string | undefined>(undefined);
    const { identityVerification: brandIdentityVerification } = useBrand();
    const { sendEvent } = useAnalytics({ eventSource: '', useJWT: true });

    if (!brandIdentityVerification) {
        log.error('No brand identity verification config found');
        return null;
    }

    const searchParams = new URLSearchParams(window.location.search);

    const errorHistoryPush = useErrorHistoryPush();
    const validateRedirectUrl = useValidatedUrl(() =>
        errorHistoryPush(AuthenticatedRoutes.VerificationRoutes.ContactCustomerService, 'Invalid verification redirect URI'),
    );

    // Get the redirect URI from the query params
    const redirectUriParam = searchParams.get(OPENID_PARAMS.REDIRECT_URI);
    const redirectUrlParam = searchParams.get(OPENID_PARAMS.REDIRECT_URL);
    const redirectUri = redirectUriParam || redirectUrlParam;

    if (!redirectUri) {
        errorHistoryPush(AuthenticatedRoutes.VerificationRoutes.ContactCustomerService, 'Missing redirect URI');
        return null;
    }

    const azureUserId = accounts?.[0]?.localAccountId || '';
    const verificationSessionCookieName = getVerificationSessionCookieName(VENDOR_NAME, azureUserId); // In the future, this could loop over multiple vendors if needed
    const [verificationSessionId] = useCookie(verificationSessionCookieName);
    const [pageState, setPageState] = useState<PageState>(verificationSessionId ? PageState.Loading : PageState.NewSession);

    const checkForExistingSession = async () => {
        const jwt = await getAccessToken(instance, accounts, b2cTenantConfig.scopes, log);
        setJwt(jwt);

        sendEvent(AmplitudeEventType.IdentityVerification_MethodSelection, {
            activation_method_shown: toAnalyticsActivationMethodList(identityVerification.ui.buttons),
            entry_flow: accounts[0]?.idTokenClaims?.newUser ? 'New Account creation' : 'Existing Account',
        });

        // Update the page state based on the clear session cookie
        if (verificationSessionId) {
            const response = await getIdentityVerificationSession(jwt, pimsApiUrl, VENDOR_NAME, verificationSessionId);

            if (response.status === SessionStatus.IN_PROGRESS && response.token) {
                setPageState(PageState.ExistingSession);
                setToken(response.token);
            } else if (response.status === SessionStatus.COMPLETED && response.redirectUrl) {
                // A user with an already completed and successful verification session should proceed to the verification redirect page
                const validatedRedirectUrl = await validateRedirectUrl(response.redirectUrl);
                if (validatedRedirectUrl) {
                    setPageState(PageState.Loading);
                    window.location.replace(validatedRedirectUrl);
                }
            } else {
                setPageState(PageState.NewSession);
            }
        }
    };

    useEffect(() => {
        checkForExistingSession();
    }, [verificationSessionId]);

    const updateSessionCookie = (verificationSessionId: string) => {
        setCookie(verificationSessionCookieName, verificationSessionId);
    };

    const componentsProps = identityVerification.ui;
    const classes = useStyles();
    const isBulletPointsList = componentsProps.list?.length > 0;

    return (
        <IdentityVerificationCard
            pageState={pageState}
            isBulletPointsList={isBulletPointsList}
            classes={classes}
            componentsProps={componentsProps}
            updateSessionCookie={updateSessionCookie}
            jwt={jwt}
            token={token}
            isCrossRegion={isCrossRegion}
        />
    );
};

type IdentityVerificationCardProps = {
    pageState: PageState;
    isBulletPointsList: boolean;
    classes: any;
    componentsProps: any;
    updateSessionCookie: (a: string) => void;
    jwt: string;
    isCrossRegion: boolean;
    token?: string;
};

export function IdentityVerificationCard({
    pageState,
    isBulletPointsList,
    classes,
    componentsProps,
    updateSessionCookie,
    jwt,
    token,
    isCrossRegion,
}: IdentityVerificationCardProps): JSX.Element {
    const configT = useConfigTranslate();

    return (
        <BrandedCard maxWidth={452} minHeight={isBulletPointsList ? 530 : 450} isLoading={pageState === PageState.Loading}>
            <BrandedCardContent marginTop={48} height={isBulletPointsList ? 286 : 200} verticallyCentered={isBulletPointsList ? false : true}>
                <Typography variant={isBulletPointsList ? 'subtitle1' : 'h2'} className={classes.header} align={isBulletPointsList ? 'justify' : 'center'}>
                    {configT(componentsProps.header)}
                </Typography>
                {isBulletPointsList ? <BulletPointsList items={componentsProps.list} /> : null}
            </BrandedCardContent>
            <BrandedCardActions direction='column' gap={16} height={214} justifyContent='flex-start'>
                <CardActions
                    buttonsList={componentsProps.buttons}
                    updateSessionCookie={updateSessionCookie}
                    jwt={jwt}
                    token={token} // Clear Token
                    isCrossRegion={isCrossRegion}
                />
            </BrandedCardActions>
        </BrandedCard>
    );
}

export default withLogger(IdentityVerificationPage, {
    eventSource: 'IdentityVerificationPage',
});
